/**
 * Modulation Lab v1 — pure analyzer for cross-key modulation training.
 *
 * Two sub-modes: Modulation Explore (free-explore source × technique ×
 * destination) and Modulation ID Quiz (10-Q recognition session). Four
 * canonical techniques: direct (chromatic), pivot (shared diatonic chord),
 * common tone (held pitch through harmonic shift), parallel mode (major ↔
 * minor on same tonic).
 *
 * v1 source pool: 12 majors (flat-side enharmonics — Db not C#). v1
 * destinations: closely-related cluster (5 keys per source via
 * cof-wheel.getCloselyRelatedKeys) + parallel minor for the parallel
 * technique.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §4
 * Bowie scope memo: .claude/agent-memory/bowie/project_modulation_lab_v1_scope.md
 */

import type { NoteName } from '@tunasong/schemas'
import { ChordLib, type Chord, type ChordVariant } from '../chord/index.js'
import ScaleLib from '../scale/scale.js'
import { getCloselyRelatedKeys, getRelativeKey } from './cof-wheel.js'

export const SOURCE_KEY_POOL = [
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'Db',
  'Ab',
  'Eb',
  'Bb',
  'F',
] as const

export type SourceKey = (typeof SOURCE_KEY_POOL)[number]

/**
 * v1.5 minor source pool — natural-minor sources with sharp-side conventional
 * spellings (F♯m not G♭m), except the four flat-tonic minors (Ebm, Bbm, Fm, Cm,
 * Gm, Dm) whose tonics are conventionally flat. Bowie-locked.
 */
export const MINOR_SOURCE_KEY_POOL = [
  'Am', 'Em', 'Bm', 'F#m', 'C#m', 'G#m', 'Ebm', 'Bbm', 'Fm', 'Cm', 'Gm', 'Dm',
] as const

export type MinorSourceKey = (typeof MINOR_SOURCE_KEY_POOL)[number]

/** Full v1.5 source pool: 12 majors then 12 minors. The quiz draws from this. */
export const ALL_SOURCE_KEYS = [...SOURCE_KEY_POOL, ...MINOR_SOURCE_KEY_POOL] as const

export type AnySourceKey = (typeof ALL_SOURCE_KEYS)[number]

export const MODULATION_TECHNIQUES = ['direct', 'pivot', 'common-tone', 'parallel', 'deceptive'] as const
export type ModulationTechnique = (typeof MODULATION_TECHNIQUES)[number]

/**
 * Relative major of a minor key, correctly spelled. The relative major is the
 * ♭III triad of the natural minor (degree 3), so we read it straight off the
 * diatonic chord set — this inherits ScaleLib's enharmonic decisions (Cm → Eb,
 * not D#).
 */
export const relativeMajorOf = (minorKey: string): string => getDiatonicChordsForKey(minorKey)[2].name

/** Parallel major of a minor key = same tonic, major mode (Am → A). */
const parallelMajorOf = (minorKey: string): string => stripMinor(minorKey)

export const getValidDestinations = (
  source: string,
  technique: ModulationTechnique
): readonly string[] => {
  const minorSource = isMinorKey(source)
  if (technique === 'parallel') {
    return minorSource ? [parallelMajorOf(source)] : [`${source}m`]
  }
  if (technique === 'deceptive') {
    // Major source → relative minor; minor source → relative major.
    return minorSource ? [relativeMajorOf(source)] : [`${getRelativeKey(source, 'major').name}m`]
  }
  if (minorSource) {
    // Minor-source closely-related cluster = relative major + its neighbours,
    // with the source itself removed and the relative major prepended. Yields a
    // 5-key cluster (Bowie-locked simplification).
    const relMaj = relativeMajorOf(source)
    const neighbours = getCloselyRelatedKeys(relMaj).filter(k => k !== source && k !== relMaj)
    return [relMaj, ...neighbours].slice(0, 5)
  }
  return getCloselyRelatedKeys(source)
}

// ─── Diatonic chord lookup ───────────────────────────────────────────────────

export interface DiatonicChord {
  /** Chord name as commonly spelled (e.g. 'C', 'Dm', 'Bdim', 'Am'). */
  name: string
  degree: 1 | 2 | 3 | 4 | 5 | 6 | 7
  quality: 'maj' | 'min' | 'dim'
}

/** Triad quality on each major-scale degree. */
const MAJOR_DIATONIC_QUALITIES = ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim'] as const

/** Triad quality on each natural-minor-scale degree (rotation of major by 5). */
const MINOR_DIATONIC_QUALITIES = ['min', 'dim', 'maj', 'min', 'min', 'maj', 'maj'] as const

const isMinorKey = (key: string): boolean => key.endsWith('m') && !key.endsWith('dim')
const stripMinor = (key: string): string => key.replace(/m$/, '')

const qualitySuffix = (q: 'maj' | 'min' | 'dim'): string => {
  if (q === 'maj') {return ''}
  if (q === 'min') {return 'm'}
  return 'dim'
}

/**
 * Compute the seven diatonic triads for a key. Spelling is delegated to
 * `ScaleLib.getNotes` so the chord roots inherit the scale's enharmonic
 * decisions — keys with flats in their tonic (Bb, Eb, Ab, Db, F, plus minor
 * tonics from the relative-minor cluster) keep flat-side spellings; sharp
 * keys keep sharp-side spellings.
 */
export const getDiatonicChordsForKey = (key: string): DiatonicChord[] => {
  const minor = isMinorKey(key)
  const tonicName = (minor ? stripMinor(key) : key) as NoteName
  const qualities = minor ? MINOR_DIATONIC_QUALITIES : MAJOR_DIATONIC_QUALITIES
  const scaleNotes = ScaleLib.getNotes({
    root: tonicName,
    type: minor ? 'minor' : 'major',
  })
  return scaleNotes.slice(0, 7).map((root, i) => {
    const quality = qualities[i]
    return {
      name: `${root}${qualitySuffix(quality)}`,
      degree: (i + 1) as DiatonicChord['degree'],
      quality,
    }
  })
}

// ─── Pivot chord selection ───────────────────────────────────────────────────

export interface PivotChord {
  name: string
  sourceFunction: { degree: number; quality: 'maj' | 'min' | 'dim' }
  destinationFunction: { degree: number; quality: 'maj' | 'min' | 'dim' }
  functionDistance: number
}

const functionDistance = (a: number, b: number): number => {
  // Scale-degree distance with wraparound (degrees 1..7).
  const direct = Math.abs(a - b)
  const wrap = 7 - direct
  return Math.min(direct, wrap)
}

/**
 * Bowie-locked pivot table for the 5 C-source rows published in the scope
 * memo. The memo's C-source row is the canonical training example.
 *
 * For source/destination pairs NOT in this table, the algorithmic default
 * applies (see comments below). Notably, G→C falls through to the default,
 * which selects pivot = C (IV → I) — the standard pivot-to-IV pattern.
 *
 * Locked verbatim — do not edit without Bowie review.
 */
const BOWIE_LOCKED_PIVOT_TABLE: Record<string, string> = {
  'C->G': 'C',
  'C->F': 'F',
  'C->Am': 'Am',
  'C->Dm': 'Dm',
  'C->Em': 'Em',
  // v1.5 minor-source Am rows (5 destinations in Am's closely-related cluster).
  // Each pivot is a reinterpreted diatonic chord shared by both keys. Bowie-
  // locked; the algorithmic default holds for the other 11 minor sources.
  'Am->C': 'C', // ♭III/Am → I/C
  'Am->F': 'F', // ♭VI/Am → I/F
  'Am->G': 'G', // ♭VII/Am → I/G
  'Am->Dm': 'Dm', // iv/Am → i/Dm
  'Am->Em': 'Am', // i/Am → iv/Em
}

export const selectPivotChord = (source: string, destination: string): PivotChord | null => {
  const sourceChords = getDiatonicChordsForKey(source)
  const destinationChords = getDiatonicChordsForKey(destination)
  const sourceByName = new Map(sourceChords.map(c => [c.name, c]))

  // Shared = chords present in both keys' diatonic sets (by chord name).
  const shared: PivotChord[] = []
  for (const destChord of destinationChords) {
    const sourceChord = sourceByName.get(destChord.name)
    if (!sourceChord) {continue}
    shared.push({
      name: destChord.name,
      sourceFunction: { degree: sourceChord.degree, quality: sourceChord.quality },
      destinationFunction: { degree: destChord.degree, quality: destChord.quality },
      functionDistance: functionDistance(sourceChord.degree, destChord.degree),
    })
  }
  if (shared.length === 0) {return null}

  const findByName = (name: string): PivotChord | undefined => shared.find(s => s.name === name)

  // 1. Bowie-locked table override — covers the 6 published rows verbatim.
  const lockedPivot = BOWIE_LOCKED_PIVOT_TABLE[`${source}->${destination}`]
  if (lockedPivot) {
    const match = findByName(lockedPivot)
    if (match) {return match}
  }

  // 2. Algorithmic default for un-locked (source, destination) pairs. The
  // pattern observed in Bowie's C-source row is:
  //
  //   - destination = V/source (major destination)  → pivot = I/source
  //   - destination = IV/source (major destination) → pivot = I/destination
  //   - destination is the relative minor of source's IV / V / I
  //     (i.e., vi/ii/iii of source)                 → pivot = I/destination
  //
  // This is the "default rule" applied to the 11 non-C source keys.
  const sourceTonicName = sourceChords[0].name
  const destinationTonicName = destinationChords[0].name
  const destAsSourceDegree = sourceByName.get(destinationTonicName)?.degree
  const sourceTonicAsPivot = findByName(sourceTonicName)
  const destinationTonicAsPivot = findByName(destinationTonicName)

  if (destAsSourceDegree === 5 && sourceTonicAsPivot) {
    return sourceTonicAsPivot
  }
  if (destinationTonicAsPivot) {
    return destinationTonicAsPivot
  }
  // Pathological fallback — shouldn't reach this for valid closely-related
  // destinations, but fall through to the first shared chord (deterministic)
  // rather than throwing.
  return shared[0]
}

// ─── Dominant + common-tone helpers ──────────────────────────────────────────

export interface DominantChord {
  name: string
  root: string
}

export const getDominantOfKey = (destinationKey: string): DominantChord => {
  const minor = isMinorKey(destinationKey)
  const tonicName = (minor ? stripMinor(destinationKey) : destinationKey) as NoteName
  // V root = the 5th scale degree of the destination key (perfect 5th above the
  // tonic). For minor keys this resolves to the natural-minor V (a minor
  // triad's root pitch with the analyzer reporting a MAJOR-quality V — the
  // harmonic-minor leading-tone V chord, the canonical key-establishment chord).
  const scaleNotes = ScaleLib.getNotes({
    root: tonicName,
    type: minor ? 'minor' : 'major',
  })
  const root = scaleNotes[4]
  return { name: root, root }
}

/** Resolve a chord-name to its note set via the chord library. */
const chordNotesForName = (name: string): NoteName[] => {
  const variant = ChordLib.fromName(name)
  if (!variant) {return []}
  return ChordLib.getNotes(variant)
}

export const getCommonTone = (source: string, destination: string): NoteName | null => {
  const sourceTonicChord = getDiatonicChordsForKey(source)[0]
  const destinationTonicChord = getDiatonicChordsForKey(destination)[0]
  const sourceNotes = new Set(chordNotesForName(sourceTonicChord.name))
  const destinationNotes = chordNotesForName(destinationTonicChord.name)
  for (const n of destinationNotes) {
    if (sourceNotes.has(n)) {return n}
  }
  return null
}

// ─── Plan builder ─────────────────────────────────────────────────────────────

export interface ModulationPlan {
  sourceKey: string
  destinationKey: string
  technique: ModulationTechnique
  sourceProgression: Chord[]
  modulationProgression: Chord[]
  destinationProgression: Chord[]
  pivotChord?: Chord
  commonTone?: NoteName
}

/**
 * Resolve a chord name (e.g. 'C', 'Cm', 'Bdim', 'F#dim') to a ChordVariant. The
 * lookup goes through ChordLib.fromName which tries parseChordString first then
 * the all-chords library. v1 chord names are always plain triads — if a name
 * ever fails to resolve, that's a builder bug surfaced by tests, not a runtime
 * crash to swallow.
 */
const chordFromName = (name: string): ChordVariant => {
  const chord = ChordLib.fromName(name)
  if (!chord) {
    throw new Error(`modulation-lab: could not parse chord name '${name}'`)
  }
  return chord
}

export type AuditionLength = 'standard' | 'short'

const buildVamp = (key: string, length: AuditionLength = 'standard'): Chord[] => {
  const dia = getDiatonicChordsForKey(key)
  if (isMinorKey(key)) {
    const i = dia[0]
    const bVI = dia[5]
    const bVII = dia[6]
    if (length === 'short') {
      // Short minor vamp: i-♭VII — a 2-bar sub-tonic motion to establish the key.
      return [chordFromName(i.name), chordFromName(bVII.name)]
    }
    // Standard: i-♭VI-♭VII-i (natural-minor analogue per Bowie §8.7).
    return [chordFromName(i.name), chordFromName(bVI.name), chordFromName(bVII.name), chordFromName(i.name)]
  }
  const I = dia[0]
  const V = dia[4]
  const vi = dia[5]
  const IV = dia[3]
  if (length === 'short') {
    // Short major vamp: I-V — a 2-bar dominant motion to establish the key.
    return [chordFromName(I.name), chordFromName(V.name)]
  }
  // Standard: I-V-vi-IV.
  return [chordFromName(I.name), chordFromName(V.name), chordFromName(vi.name), chordFromName(IV.name)]
}

const buildPivotPhase = (source: string, destination: string): { phase: Chord[]; pivot: Chord } => {
  const pivot = selectPivotChord(source, destination)
  if (!pivot) {
    throw new Error(`modulation-lab: no pivot chord between ${source} and ${destination}`)
  }
  const dom = getDominantOfKey(destination)
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // [pivot, pivot, V/dest, I/dest]
  return {
    phase: [
      chordFromName(pivot.name),
      chordFromName(pivot.name),
      chordFromName(dom.name),
      chordFromName(destTonic.name),
    ],
    pivot: chordFromName(pivot.name),
  }
}

const buildDirectPhase = (source: string, destination: string): Chord[] => {
  const sourceTonic = getDiatonicChordsForKey(source)[0]
  const dom = getDominantOfKey(destination)
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // [I/source, V/dest, V/dest, I/dest] — the V/dest appears abruptly after the
  // source tonic, then re-asserts before resolving.
  return [
    chordFromName(sourceTonic.name),
    chordFromName(dom.name),
    chordFromName(dom.name),
    chordFromName(destTonic.name),
  ]
}

const buildCommonTonePhase = (
  source: string,
  destination: string
): { phase: Chord[]; commonTone: NoteName } => {
  const ct = getCommonTone(source, destination)
  if (!ct) {
    throw new Error(`modulation-lab: no common tone between ${source} and ${destination}`)
  }
  const sourceTonic = getDiatonicChordsForKey(source)[0]
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // Bridge 1: source-diatonic chord (not I/source) that contains the CT, AND is
  // not the destination tonic (so the bridge does not pre-announce the arrival
  // chord). Falls back to the v1 first-match if the filter exhausts candidates.
  const sourceDia = getDiatonicChordsForKey(source).slice(1)
  const containsCt = (name: string): boolean => chordNotesForName(name).includes(ct)
  const bridge1 =
    sourceDia.find(c => containsCt(c.name) && c.name !== destTonic.name) ??
    sourceDia.find(c => containsCt(c.name)) ??
    sourceDia[0]
  // Bridge 2: dest-diatonic chord (not I/dest) that contains the CT, AND differs
  // from bridge1 (so the two bridges are not the same chord — needed for
  // C→G, which would otherwise double Em). Falls back to the v1 first-match.
  const destDia = getDiatonicChordsForKey(destination).slice(1)
  const bridge2 =
    destDia.find(c => containsCt(c.name) && c.name !== bridge1.name) ??
    destDia.find(c => containsCt(c.name)) ??
    destDia[0]
  // [I/source, bridge1, bridge2, I/dest] — every chord contains the CT (the two
  // tonics by definition, the bridges by selection).
  return {
    phase: [
      chordFromName(sourceTonic.name),
      chordFromName(bridge1.name),
      chordFromName(bridge2.name),
      chordFromName(destTonic.name),
    ],
    commonTone: ct,
  }
}

const buildParallelPhase = (source: string): Chord[] => {
  // Major source (e.g. C): [I major, i minor, iv minor, i minor] = [C, Cm, Fm,
  // Cm]. The major→minor pivot is the audible 3rd-degree shift (E → E♭).
  //
  // Minor source (e.g. Am): mirror — [i minor, I major, IV major, I major] =
  // [Am, A, D, A]. The minor→major pivot is the 3rd-degree shift (C → C♯).
  //
  // Scale spelling is delegated to ScaleLib so flat/sharp tonics keep their
  // conventional spelling.
  if (isMinorKey(source)) {
    const tonic = stripMinor(source)
    const minor = chordFromName(source)
    const major = chordFromName(tonic)
    const parallelMajorScale = ScaleLib.getNotes({ root: tonic as NoteName, type: 'major' })
    const ivRoot = parallelMajorScale[3]
    const ivChord = chordFromName(`${ivRoot}`)
    return [minor, major, ivChord, major]
  }
  const major = chordFromName(source)
  const minor = chordFromName(`${source}m`)
  // iv of source minor = 4th scale degree of source's parallel minor.
  const parallelMinorScale = ScaleLib.getNotes({ root: source as NoteName, type: 'minor' })
  const ivRoot = parallelMinorScale[3]
  const ivChord = chordFromName(`${ivRoot}m`)
  return [major, minor, ivChord, minor]
}

/**
 * Deceptive modulation phase. The dominant of the source resolves not to its
 * tonic but to vi, which is reinterpreted as i of the relative minor (the new
 * key) — V→vi substitutes for V→I, completing the modulation in one chord.
 *
 * Major source (e.g. C → Am): [V/source, vi/source, vi/source, V/dest] =
 * [G, Am, Am, Em]. The vi/source (Am) IS i/dest, re-asserted then confirmed by
 * the destination's own v.
 *
 * Minor source (e.g. Am → C): [V/source (harmonic-minor major triad),
 * I/dest, I/dest, V/dest] = [E, C, C, G]. The harmonic-minor V (E major)
 * deceptively reaches the relative major (C = ♭III/Am), which becomes the new I.
 */
const buildDeceptivePhase = (source: string, destination: string): Chord[] => {
  if (isMinorKey(source)) {
    const vOfSource = getDominantOfKey(source)
    const destTonic = getDiatonicChordsForKey(destination)[0]
    const vOfDest = getDiatonicChordsForKey(destination)[4]
    return [
      chordFromName(vOfSource.name),
      chordFromName(destTonic.name),
      chordFromName(destTonic.name),
      chordFromName(vOfDest.name),
    ]
  }
  const sourceDia = getDiatonicChordsForKey(source)
  const v = sourceDia[4]
  const vi = sourceDia[5]
  const vOfDest = getDiatonicChordsForKey(destination)[4]
  return [
    chordFromName(v.name),
    chordFromName(vi.name),
    chordFromName(vi.name),
    chordFromName(vOfDest.name),
  ]
}

export const buildModulationPlan = (input: {
  sourceKey: string
  destinationKey: string
  technique: ModulationTechnique
  /** v1.5 audition length. Standard = 4-bar vamps; short = 2-bar vamps. */
  length?: AuditionLength
}): ModulationPlan => {
  const { sourceKey, destinationKey, technique, length = 'standard' } = input
  const sourceProgression = buildVamp(sourceKey, length)
  const destinationProgression = buildVamp(destinationKey, length)

  if (technique === 'pivot') {
    const { phase, pivot } = buildPivotPhase(sourceKey, destinationKey)
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: phase,
      destinationProgression,
      pivotChord: pivot,
    }
  }
  if (technique === 'direct') {
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: buildDirectPhase(sourceKey, destinationKey),
      destinationProgression,
    }
  }
  if (technique === 'common-tone') {
    const { phase, commonTone } = buildCommonTonePhase(sourceKey, destinationKey)
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: phase,
      destinationProgression,
      commonTone,
    }
  }
  if (technique === 'deceptive') {
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: buildDeceptivePhase(sourceKey, destinationKey),
      destinationProgression,
    }
  }
  // parallel
  return {
    sourceKey,
    destinationKey,
    technique,
    sourceProgression,
    modulationProgression: buildParallelPhase(sourceKey),
    destinationProgression,
  }
}

// ─── Quiz machinery ───────────────────────────────────────────────────────────

export interface ModulationQuizQuestion {
  plan: ModulationPlan
  acceptedAnswer: ModulationTechnique
}

export interface ModulationQuizVerdict {
  correct: boolean
  picked: ModulationTechnique
  question: ModulationQuizQuestion
}

export interface ModulationQuizResult {
  question: ModulationQuizQuestion
  verdict: ModulationQuizVerdict
}

export interface GenerateModulationQuizOptions {
  /** Source pool. Defaults to majors only (v1 behaviour); pass ALL_SOURCE_KEYS
   * to include the v1.5 minor sources. */
  sourcePool?: readonly string[]
  /** v1.5 audition length. */
  length?: AuditionLength
}

export const generateModulationQuizQuestion = (
  rng: () => number = Math.random,
  options: GenerateModulationQuizOptions = {}
): ModulationQuizQuestion => {
  const { sourcePool = SOURCE_KEY_POOL, length = 'standard' } = options
  const sourceIdx = Math.min(sourcePool.length - 1, Math.floor(rng() * sourcePool.length))
  const sourceKey = sourcePool[sourceIdx]
  const techniqueIdx = Math.min(
    MODULATION_TECHNIQUES.length - 1,
    Math.floor(rng() * MODULATION_TECHNIQUES.length)
  )
  const technique = MODULATION_TECHNIQUES[techniqueIdx]

  /**
   * `getValidDestinations` answers "which keys are closely related", which is not the same question
   * as "which keys can this technique actually reach". A common-tone modulation needs a shared tone,
   * and the closely-related cluster contains pairs that have none — `buildModulationPlan` then threw
   * for 7.5% of source/technique draws (`no common tone between Db and F#`), which surfaced as a
   * dead Hard-mode button roughly one attempt in thirteen.
   *
   * Build the plan for each candidate and keep the ones that succeed, so a question is only ever
   * generated from a destination the technique can genuinely reach.
   */
  const candidates = getValidDestinations(sourceKey, technique)
  const playable = candidates.flatMap(destinationKey => {
    try {
      return [buildModulationPlan({ sourceKey, destinationKey, technique, length })]
    } catch {
      return []
    }
  })

  if (playable.length === 0) {
    // Every destination for this pairing is unreachable. `pivot` on a major source always has one,
    // so this is a safe fallback rather than another throw.
    const plan = buildModulationPlan({
      sourceKey,
      destinationKey: getValidDestinations(sourceKey, 'pivot')[0],
      technique: 'pivot',
      length,
    })
    return { plan, acceptedAnswer: 'pivot' }
  }

  const planIdx = Math.min(playable.length - 1, Math.floor(rng() * playable.length))
  return { plan: playable[planIdx], acceptedAnswer: technique }
}

export const validateModulationQuizAnswer = (
  question: ModulationQuizQuestion,
  picked: ModulationTechnique
): ModulationQuizVerdict => ({
  correct: picked === question.acceptedAnswer,
  picked,
  question,
})

export interface ModulationSessionStats {
  total: number
  correct: number
  byTechnique: Record<ModulationTechnique, { total: number; correct: number }>
}

export const computeModulationSessionStats = (
  results: readonly ModulationQuizResult[]
): ModulationSessionStats => {
  const stats: ModulationSessionStats = {
    total: results.length,
    correct: results.filter(r => r.verdict.correct).length,
    byTechnique: {
      direct: { total: 0, correct: 0 },
      pivot: { total: 0, correct: 0 },
      'common-tone': { total: 0, correct: 0 },
      parallel: { total: 0, correct: 0 },
      deceptive: { total: 0, correct: 0 },
    },
  }
  for (const r of results) {
    const bucket = stats.byTechnique[r.question.acceptedAnswer]
    bucket.total += 1
    if (r.verdict.correct) {bucket.correct += 1}
  }
  return stats
}
